import { createGlobalStyle } from 'styled-components';
import { galleryFontFaces, galleryTextSelection } from './typography';

export const GlobalStyles = createGlobalStyle`
  /* Load gallery fonts */
  ${galleryFontFaces}
  
  /* Gallery text selection styling */
  ${galleryTextSelection}
  
  /* Fonts will be loaded via index.html */

  /* Reset and base styles */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    /* Safe area support for notched devices */
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height */

    /* Only enable smooth scrolling when the user has not requested less motion. */
    @media (min-width: 768px) and (hover: hover) and (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }

    --site-header-height: ${({ theme }) => theme.layout.headerHeight};
    --site-header-offset: ${({ theme }) => theme.layout.headerOffset};
  }

  body {
    font-family: ${({ theme }) => theme.typography.fontFamily.body};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.semantic.textPrimary};
    background-color: ${({ theme }) => theme.colors.semantic.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    /* Handle safe areas on notched devices */
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.fontFamily.heading};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    line-height: 1.2;
    letter-spacing: -0.01em;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  h5 {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  h6 {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }

  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  /* Links */
  a {
    color: ${({ theme }) => theme.colors.semantic.accentHover};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.fast};

    &:hover {
      color: ${({ theme }) => theme.colors.semantic.textPrimary};
    }

    &:focus-visible {
      outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
      outline-offset: 2px;
    }
  }

  /* Buttons */
  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;
    transition: all ${({ theme }) => theme.transitions.fast};

    &:focus-visible {
      outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  /* Forms */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    color: ${({ theme }) => theme.colors.semantic.textPrimary};
    background: ${({ theme }) => theme.colors.semantic.surface};
    border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    transition: border-color ${({ theme }) => theme.transitions.fast};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.semantic.focus};
      box-shadow: ${({ theme }) => theme.shadows.gallery.focus};
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.semantic.textMuted};
    }
  }

  /* Keep native controls readable in the dark admin and public surfaces. */
  button, input, textarea, select {
    color-scheme: dark;
  }

  /* Lists */
  ul, ol {
    padding-left: ${({ theme }) => theme.spacing.lg};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Layout helpers */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.md};

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      padding: 0 ${({ theme }) => theme.spacing.lg};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
      padding: 0 ${({ theme }) => theme.spacing.xl};
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Grid system */
  .grid {
    display: grid;
    gap: ${({ theme }) => theme.spacing.md};
  }

  .grid-cols-1 {
    grid-template-columns: 1fr;
  }

  .grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    .md\\:grid-cols-2 {
      grid-template-columns: repeat(2, 1fr);
    }

    .md\\:grid-cols-3 {
      grid-template-columns: repeat(3, 1fr);
    }

    .md\\:grid-cols-4 {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    .lg\\:grid-cols-3 {
      grid-template-columns: repeat(3, 1fr);
    }

    .lg\\:grid-cols-4 {
      grid-template-columns: repeat(4, 1fr);
    }

    .lg\\:grid-cols-5 {
      grid-template-columns: repeat(5, 1fr);
    }
  }

  /* Utility classes */
  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  /* Gallery-specific utility classes */
  .gallery-hero {
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.hero};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.colors.gallery.text.primary};
  }

  .gallery-section {
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    letter-spacing: 0;
    color: ${({ theme }) => theme.colors.gallery.text.primary};
  }

  .gallery-artwork-title {
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.title};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    letter-spacing: 0;
    color: ${({ theme }) => theme.colors.gallery.text.primary};
  }

  .gallery-metadata {
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.gallery.neutrals.dark};
  }

  .gallery-caption {
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.caption};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
    color: ${({ theme }) => theme.colors.gallery.neutrals.medium};
  }

  /* Joanne Art Class color utilities */
  .gallery-bg-white {
    background-color: ${({ theme }) => theme.colors.gallery.white};
  }

  .gallery-bg-card {
    background-color: ${({ theme }) => theme.colors.gallery.card};
  }

  .gallery-bg-secondary {
    background-color: ${({ theme }) => theme.colors.gallery.secondary};
  }

  .gallery-bg-soft {
    background-color: ${({ theme }) => theme.colors.gallery.neutrals.lightest};
  }

  .gallery-text-charcoal {
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  }

  .gallery-text-primary {
    color: ${({ theme }) => theme.colors.gallery.accent.primary};
  }

  .gallery-text-secondary {
    color: ${({ theme }) => theme.colors.gallery.accent.secondary};
  }

  .gallery-text-neutral {
    color: ${({ theme }) => theme.colors.gallery.neutrals.medium};
  }

  .gallery-text-muted {
    color: ${({ theme }) => theme.colors.gallery.neutrals.dark};
  }

  .mb-0 { margin-bottom: 0; }
  .mb-sm { margin-bottom: ${({ theme }) => theme.spacing.sm}; }
  .mb-md { margin-bottom: ${({ theme }) => theme.spacing.md}; }
  .mb-lg { margin-bottom: ${({ theme }) => theme.spacing.lg}; }
  .mb-xl { margin-bottom: ${({ theme }) => theme.spacing.xl}; }

  .mt-0 { margin-top: 0; }
  .mt-sm { margin-top: ${({ theme }) => theme.spacing.sm}; }
  .mt-md { margin-top: ${({ theme }) => theme.spacing.md}; }
  .mt-lg { margin-top: ${({ theme }) => theme.spacing.lg}; }
  .mt-xl { margin-top: ${({ theme }) => theme.spacing.xl}; }
  
  /* Mobile UX enhancements */
  
  /* Safe area utilities */
  .safe-top { padding-top: env(safe-area-inset-top); }
  .safe-bottom { padding-bottom: env(safe-area-inset-bottom); }
  .safe-left { padding-left: env(safe-area-inset-left); }
  .safe-right { padding-right: env(safe-area-inset-right); }
  .safe-all { 
    padding-top: env(safe-area-inset-top);
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
  }
  
  /* Dynamic viewport height helpers */
  .dvh-100 { height: 100dvh; }
  .min-dvh-100 { min-height: 100dvh; }
  
  /* Mobile-optimized interactions */
  .touch-none { touch-action: none; }
  .touch-pan-x { touch-action: pan-x; }
  .touch-pan-y { touch-action: pan-y; }
  .touch-manipulation { touch-action: manipulation; }
  
  /* Accessibility and reduced motion */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* The shared public shell applies the fixed header offset exactly once. */
  .public-shell {
    min-height: 100%;
    padding-top: var(--site-header-offset);
  }
  
  /* Dark mode preparation */
  @media (prefers-color-scheme: dark) {
    :root {
      color-scheme: dark;
    }
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    :root {
      filter: contrast(1.2);
    }
  }
`;
